import React from "react";
import _ from "lodash";
import AbstractEntity from "./abstractEntity";

/*
gameObj properties:
  - id
  - leagueSeason (id)
  - week
  - isNeutral
  - hasStarted
  - hasEnded
  - away (gameTeamObj)
  - home (gameTeamObj)
  - [scoreDiff]
  - [name]
  - [oppType]
  - [playoffRound]
 */
class Game extends AbstractEntity {
  /**
   *
   * @param {object} gameObj
   * @param {UserInfo} userInfo
   */
  constructor(gameObj, userInfo) {
    super(gameObj, userInfo);

    this.away = null;
    this.home = null;

    if (this.hasLoaded()) {
      this.vs = this.obj.isNeutral ? "vs" : "at";

      // todo: improve these
      this.label = "TBD vs TBD";
      this.pathDeps = {};
      this.defaultPathName = "game";
    }
  }

  setLabel() {
    const awayLabel = this.away?.team?.abbr || "TBD";
    const homeLabel = this.home?.team?.abbr || "TBD";
    this.label = `${awayLabel} ${this.vs} ${homeLabel}`;
  }

  getGameTeam(teamId) {
    this.verifyHasTeam(teamId, "getOppGameTeam");
    return this.isHome(teamId) ? this.home : this.away;
  }

  /**
   *
   * @param {string} teamId
   * @returns {GameTeam|null}
   */
  getOppGameTeam(teamId) {
    this.verifyHasTeam(teamId, "getOppGameTeam");
    return this.isHome(teamId) ? this.away : this.home;
  }

  /**
   *
   * @param {string} [teamIdFirst]
   * @returns {string}
   */
  getScore(teamIdFirst) {
    const homeScore =
      this.home.obj.hasOwnProperty("score") && !_.isNull(this.home.obj.score)
        ? this.home.obj.score
        : 0;
    const awayScore =
      this.away.obj.hasOwnProperty("score") && !_.isNull(this.away.obj.score)
        ? this.away.obj.score
        : 0;

    if (homeScore === 0 && awayScore === 0) return "";

    let first;
    if (teamIdFirst) {
      first = this.isHome(teamIdFirst) ? "home" : "away";
    } else {
      first = homeScore > awayScore ? "home" : "away";
    }

    const firstScore = first === "home" ? homeScore : awayScore;
    const secondScore = first === "home" ? awayScore : homeScore;

    return `${firstScore}-${secondScore}`;
  }

  /**
   *
   * @param {string} teamId
   * @param {string} [size="lg"]
   * @returns {JSX.Element}
   */
  getResultText(teamId, size = "lg") {
    const gameTeam = this.getGameTeam(teamId);
    const score = this.getScore(teamId);
    const resultText =
      size === "sm" ? gameTeam.getResultTextSm() : gameTeam.getResultTextLg();
    let className = "baseColor";
    if (gameTeam.obj.result === "W") className = "goodColor";
    else if (gameTeam.obj.result === "L") className = "badColor";

    const resultTag = resultText ? (
      <span className={className}>{resultText}</span>
    ) : null;

    let scoreTag = score;
    if (this.obj.hasEnded) {
      scoreTag = <span style={{ fontWeight: "bold" }}>{score}</span>;
    }

    return (
      <>
        {resultTag} {scoreTag}
      </>
    );
  }

  /**
   *
   * @param {string} teamId
   * @returns {string}
   */
  getVsForTeam(teamId) {
    this.verifyHasTeam(teamId, "getVsForTeam");
    if (this.obj.isNeutral) {
      return "vs";
    }
    return this.isHome(teamId) ? "" : "at";
  }

  /**
   *
   * @param {string} teamId
   * @returns {boolean|undefined}
   */
  hasTeam(teamId) {
    return this.away.team.obj.id === teamId || this.home.team.obj.id === teamId;
  }

  /**
   *
   * @param {string} teamId
   * @returns {boolean|undefined}
   */
  isHome(teamId) {
    this.verifyHasTeam(teamId, "isHome");
    return this.home.team.obj.id === teamId;
  }

  /**
   *
   * @param {string} teamId
   * @returns {boolean}
   */
  isWinning(teamId) {
    if (this.obj.hasEnded || !this.obj.hasStarted) return false;

    this.verifyHasTeam(teamId, "isWinning");
    const gameTeam = this.getGameTeam(teamId);
    const oppGameTeam = this.getOppGameTeam(teamId);

    const score = gameTeam.obj.score || 0;
    const oppScore = oppGameTeam.obj.score || 0;
    return score > oppScore;
  }

  /**
   *
   * @param {string} teamId
   * @param {string} [methodName="verifyHasTeam"]
   */
  verifyHasTeam(teamId, methodName = "verifyHasTeam") {
    if (!this.hasTeam(teamId)) {
      throw new Error(
        `Game.${methodName}() - ERROR - game does not have team: ${teamId}`
      );
    }
  }
}

export default Game;
